import { apiFetch, ApiError, API_BASE_URL, classifiedFetch, silentRefresh, type TokenStore } from './client.ts';
import { errorMessage } from '../lib/error-labels.ts';
import type { AddressFieldsBody } from './postal-addresses.ts';
import { uploadPracticeFile } from './files.ts';
import { contractNameFromFile, isTextContractFile } from '../lib/contract-file.ts';

/**
 * Enquiry → contract → signing API client (spec §7.1, §8 lines 1268-1276).
 * Paths verified against the LIVE routes:
 *   - create enquiry        POST /practices/:id/enquiries            (NOT POST /enquiries)
 *   - list templates        GET  /practices/:id/contract-templates
 *   - draft a contract      POST /enquiries/:id/contracts            (returns { contract })
 *   - send a contract       POST /contracts/:id/send                 (returns { contract } — v7.197: no signingToken; staff use GET /contracts/:id/preview)
 *   - public view           GET  /contracts/sign/:token              (no auth)
 *   - public sign           POST /contracts/sign/:token              (no auth, Idempotency-Key)
 *
 * The two `/contracts/sign/:token` endpoints are public - the prospect signs
 * without an account - so they bypass the authenticated `apiFetch` and use a
 * tokenless `publicFetch`. The signing POST carries a client-generated
 * `Idempotency-Key`; the server dedupes a replay to the SAME case (one case
 * under retry), so the caller MUST reuse the same key across retries.
 *
 * API_CONTRACT.md was corrected in this PR (PR 6): the enquiry create path,
 * its request body (caseType/recipientIsClient/contactRelationship, not
 * prospectName/prospectEmail), its `{ enquiry }` response, and the previously
 * undocumented `POST /enquiries/:id/contracts` draft endpoint.
 */

// ── Enums (mirror the server Zod) ──────────────────────────────────────────────

export type CaseType = 'child' | 'young_adult' | 'adult';
export type ConsentModel =
  | 'client_contact'
  | 'service_recipient'
  | 'shared'
  | 'best_interests';
export type ContactRelationship =
  | 'parent'
  | 'guardian'
  | 'service_recipient'
  | 'deputy'
  | 'other';
export type EngagingRelationship = Exclude<ContactRelationship, 'service_recipient'>;

// ── Enquiry ────────────────────────────────────────────────────────────────────

export interface CreateEnquiryBody {
  caseType: CaseType;
  recipientIsClient?: boolean;
  consentModel?: ConsentModel;
  contactRelationship?: EngagingRelationship;
  serviceRecipientFirstName?: string;
  serviceRecipientLastName?: string;
  /** Service recipient date of birth, epoch ms. */
  serviceRecipientDob?: number;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  notes?: string;
  // Tier-2 intake fields (#10).
  consultationDate?: number;
  consultationFormat?: ConsultationFormat;
  location?: string;
  settingName?: string;
  settingYearGroup?: string;
  fundingSource?: FundingSource;
  requestedHours?: string;
  preferredSchedule?: string;
  referrerType?: ReferrerType;
  /** All-or-nothing: omit rather than send a partial address (owner decision 2026-08-15). */
  address?: AddressFieldsBody;
}

export type ConsultationFormat = 'phone' | 'video' | 'in_person' | 'email';
export type FundingSource =
  | 'ehcp_la'
  | 'school'
  | 'parent'
  | 'mixed'
  | 'self'
  | 'tbc'
  | 'other';
export type ReferrerType = 'client' | 'organisation' | 'self';

export type EnquiryStatus =
  | 'draft'
  | 'contract_drafted'
  | 'contract_sent'
  | 'promoted'
  | 'closed_lost';

/**
 * Full enquiry row (snake_case - mirrors the live server row, same convention as
 * the cases API). The pipeline list and detail screens read these fields.
 */
export interface Enquiry {
  id: string;
  practice_id: string;
  owner_user_id: string;
  status: EnquiryStatus;
  case_type: CaseType;
  consent_model: ConsentModel;
  service_recipient_first_name: string | null;
  service_recipient_last_name: string | null;
  service_recipient_dob: number | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  contact_relationship: ContactRelationship;
  referral_source_id: string | null;
  notes: string | null;
  promoted_case_id: string | null;
  close_reason: string | null;
  created_at: number;
  updated_at: number | null;
  // Tier-2 intake fields (#10).
  consultation_date: number | null;
  consultation_format: ConsultationFormat | null;
  location: string | null;
  setting_name: string | null;
  setting_year_group: string | null;
  funding_source: FundingSource | null;
  requested_hours: string | null;
  preferred_schedule: string | null;
  referrer_type: ReferrerType | null;
  /**
   * The in-flight signing contract's id, present only on the single-enquiry GET
   * while `status === 'contract_sent'` - lets the practice resend the signing
   * link. Absent (undefined) on list responses; null when no sent contract.
   */
  sentContractId?: string | null;
  /** The address the contract itself carries - where the next resend goes. */
  sentContractEmail?: string | null;
  /** False when no issued link still works (revoked or expired): a resend is needed. */
  sentContractSigningLinkLive?: boolean;
  sentContractLead?: { kind: 'later' } | { kind: 'member'; name: string } | { kind: 'invite'; email: string | null } | null;
}

/** Fields a PATCH /enquiries/:id may change (all optional; null clears a value). */
export interface UpdateEnquiryBody {
  caseType?: CaseType;
  recipientIsClient?: boolean;
  consentModel?: ConsentModel;
  contactRelationship?: EngagingRelationship;
  serviceRecipientFirstName?: string | null;
  serviceRecipientLastName?: string | null;
  serviceRecipientDob?: number | null;
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  notes?: string | null;
  // Tier-2 intake fields (#1) - the PATCH schema already accepts these; the edit
  // form simply never surfaced them, so wizard-captured data could not be edited.
  consultationDate?: number | null;
  consultationFormat?: ConsultationFormat | null;
  location?: string | null;
  settingName?: string | null;
  settingYearGroup?: string | null;
  fundingSource?: FundingSource | null;
  requestedHours?: string | null;
  preferredSchedule?: string | null;
  referrerType?: ReferrerType | null;
}

export async function createEnquiry(
  practiceId: string,
  body: CreateEnquiryBody,
  store: TokenStore,
): Promise<Enquiry> {
  const res = await apiFetch<{ enquiry: Enquiry }>(
    `/practices/${encodeURIComponent(practiceId)}/enquiries`,
    { method: 'POST', body: JSON.stringify(body) },
    store,
  );
  return res.enquiry;
}

export async function listEnquiries(
  practiceId: string,
  store: TokenStore,
): Promise<Enquiry[]> {
  const res = await apiFetch<{ enquiries: Enquiry[] }>(
    `/practices/${encodeURIComponent(practiceId)}/enquiries`,
    {},
    store,
  );
  return res.enquiries;
}

export async function getEnquiry(enquiryId: string, store: TokenStore): Promise<Enquiry> {
  const res = await apiFetch<{ enquiry: Enquiry }>(
    `/enquiries/${encodeURIComponent(enquiryId)}`,
    {},
    store,
  );
  return res.enquiry;
}

export async function updateEnquiry(
  enquiryId: string,
  body: UpdateEnquiryBody,
  store: TokenStore,
): Promise<Enquiry> {
  const res = await apiFetch<{ enquiry: Enquiry }>(
    `/enquiries/${encodeURIComponent(enquiryId)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    store,
  );
  return res.enquiry;
}

export async function closeEnquiry(
  enquiryId: string,
  reason: string,
  store: TokenStore,
): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/enquiries/${encodeURIComponent(enquiryId)}/close`,
    { method: 'POST', body: JSON.stringify({ reason }) },
    store,
  );
}

// ── Contract templates ─────────────────────────────────────────────────────────

export interface ContractTemplate {
  id: string;
  name: string;
  scope: string;
  status: string;
  is_default: number;
}

export async function listContractTemplates(
  practiceId: string,
  store: TokenStore,
): Promise<ContractTemplate[]> {
  const res = await apiFetch<{ templates: ContractTemplate[] }>(
    `/practices/${encodeURIComponent(practiceId)}/contract-templates`,
    {},
    store,
  );
  return res.templates;
}

/**
 * Create a practice-scoped contract template against an uploaded file — the path
 * for document contracts (PDF/Word), whose bytes are served to the signing page
 * rather than rendered as text. `oneOff` creates it archived (single-use), so a
 * one-off contract need not be a text file.
 */
export async function createContractTemplate(
  practiceId: string,
  body: { name: string; fileObjectId: string; oneOff?: boolean },
  store: TokenStore,
): Promise<ContractTemplate> {
  const res = await apiFetch<{ template: ContractTemplate }>(
    `/practices/${encodeURIComponent(practiceId)}/contract-templates`,
    {
      method: 'POST',
      body: JSON.stringify({
        scope: 'practice',
        name: body.name,
        fileObjectId: body.fileObjectId,
        ...(body.oneOff === true ? { oneOff: true } : {}),
      }),
    },
    store,
  );
  return res.template;
}

/**
 * Upload a contract template from raw text (item 5), stored and shown exactly as
 * written. A body containing a {{...}} token is refused: the thrown ApiError
 * carries code 'PLACEHOLDERS_UNSUPPORTED'; the caller surfaces the message.
 */
export async function uploadContractTemplate(
  practiceId: string,
  body: {
    name: string;
    content: string;
    isDefault?: boolean;
    oneOff?: boolean;
  },
  store: TokenStore,
): Promise<ContractTemplate> {
  const res = await apiFetch<{ template: ContractTemplate }>(
    `/practices/${encodeURIComponent(practiceId)}/contract-templates/upload`,
    { method: 'POST', body: JSON.stringify({ scope: 'practice', ...body }) },
    store,
  );
  return res.template;
}

/** Make a contract template the practice default (owner-only). No body. */
export async function setDefaultContractTemplate(
  practiceId: string,
  templateId: string,
  store: TokenStore,
): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/practices/${encodeURIComponent(practiceId)}/contract-templates/${encodeURIComponent(templateId)}/set-default`,
    { method: 'POST' },
    store,
  );
}

/** Archive a contract template (reason required, invariant 6). */
export async function archiveContractTemplate(
  practiceId: string,
  templateId: string,
  reason: string,
  store: TokenStore,
): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/practices/${encodeURIComponent(practiceId)}/contract-templates/${encodeURIComponent(templateId)}/archive`,
    { method: 'POST', body: JSON.stringify({ reason }) },
    store,
  );
}

/** Rename a contract template (owner-only - V6 gap 6-C). */
export async function renameContractTemplate(
  practiceId: string,
  templateId: string,
  name: string,
  store: TokenStore,
): Promise<ContractTemplate> {
  const res = await apiFetch<{ template: ContractTemplate }>(
    `/practices/${encodeURIComponent(practiceId)}/contract-templates/${encodeURIComponent(templateId)}`,
    { method: 'PATCH', body: JSON.stringify({ name }) },
    store,
  );
  return res.template;
}

export interface ContractTemplatePreview {
  name: string;
  isText: boolean;
  rawText: string | null;
}

/** Preview a contract template - the raw text, exactly as stored (6-B). */
export async function previewContractTemplate(
  practiceId: string,
  templateId: string,
  store: TokenStore,
): Promise<ContractTemplatePreview> {
  const res = await apiFetch<{ preview: ContractTemplatePreview }>(
    `/practices/${encodeURIComponent(practiceId)}/contract-templates/${encodeURIComponent(templateId)}/preview`,
    {},
    store,
  );
  return res.preview;
}

export type ContractTemplateFileResult =
  | { readonly ok: true; readonly url: string }
  | { readonly ok: false; readonly reason: 'signed-out' | 'forbidden' | 'unavailable'; readonly code?: string };

/** Authenticated file GET (retries once after a silent refresh on 401); shared by every file-preview route. */
async function fetchAuthenticatedFileBlob(
  path: string,
  store: TokenStore,
): Promise<ContractTemplateFileResult> {
  const url = `${API_BASE_URL}${path}`;
  const fetchOnce = (token: string | null): Promise<Response> =>
    classifiedFetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include',
    }, { timeoutMs: 300_000 });

  let res = await fetchOnce(store.getToken());
  if (res.status === 401) {
    const refreshed = await silentRefresh();
    if (refreshed) {
      store.onRefresh(refreshed);
      res = await fetchOnce(refreshed.accessToken);
    } else {
      store.onAuthFailure();
    }
  }
  // 401 here means the refresh above failed and onAuthFailure already signed the user out.
  // Reporting that as 'forbidden' told them their permissions had changed; they had not.
  if (res.status === 401) return { ok: false, reason: 'signed-out' };
  if (res.status === 403) return { ok: false, reason: 'forbidden' };
  if (!res.ok) {
    const code = ((await res.json().catch(() => null)) as { error?: unknown } | null)?.error;
    return { ok: false, reason: 'unavailable', ...(typeof code === 'string' && { code }) };
  }
  return { ok: true, url: URL.createObjectURL(await res.blob()) };
}

export async function fetchContractTemplateFile(
  practiceId: string,
  templateId: string,
  store: TokenStore,
): Promise<ContractTemplateFileResult> {
  return fetchAuthenticatedFileBlob(
    `/practices/${encodeURIComponent(practiceId)}/contract-templates/${encodeURIComponent(templateId)}/file`,
    store,
  );
}

// ── Contract draft + send ────────────────────────────────────────────────────

export interface CreateContractDraftBody {
  templateId: string;
  contactEmail?: string;
  contactRelationship?: ContactRelationship;
}

export interface Contract {
  id: string;
  status: string;
}

export async function createContractDraft(
  enquiryId: string,
  body: CreateContractDraftBody,
  store: TokenStore,
): Promise<Contract> {
  const res = await apiFetch<{ contract: Contract }>(
    `/enquiries/${encodeURIComponent(enquiryId)}/contracts`,
    { method: 'POST', body: JSON.stringify(body) },
    store,
  );
  return res.contract;
}

export interface SendContractResult {
  contract: Contract;
}

export type LeadPick =
  | { readonly kind: 'me' }
  | { readonly kind: 'later' }
  | { readonly kind: 'member'; readonly userId: string }
  | { readonly kind: 'invite'; readonly email: string };

export async function sendContract(
  contractId: string,
  store: TokenStore,
  lead?: LeadPick,
): Promise<SendContractResult> {
  return apiFetch<SendContractResult>(
    `/contracts/${encodeURIComponent(contractId)}/send`,
    { method: 'POST', body: JSON.stringify(lead === undefined ? {} : { lead }) },
    store,
  );
}

/** ONE home for "upload a one-off agreement" (CLAUDE.md rule 5) - shared by every
 *  entry point that offers it, enquiry and case alike. */
export async function createOneOffContractTemplateFromFile(
  practiceId: string,
  file: File,
  store: TokenStore,
): Promise<ContractTemplate> {
  const name = contractNameFromFile(file);
  return isTextContractFile(file)
    ? uploadContractTemplate(practiceId, { name, content: await file.text(), oneOff: true }, store)
    : createContractTemplate(
        practiceId,
        { name, fileObjectId: (await uploadPracticeFile(practiceId, file, store)).fileObjectId, oneOff: true },
        store,
      );
}

/** One-off send: upload the file as a single-use (archived) template, then draft + send it. */
export async function sendOneOffContract(
  practiceId: string,
  enquiryId: string,
  file: File,
  contactEmail: string | undefined,
  store: TokenStore,
  lead?: LeadPick,
): Promise<SendContractResult> {
  const tpl = await createOneOffContractTemplateFromFile(practiceId, file, store);
  const draft = await createContractDraft(enquiryId, { templateId: tpl.id, contactEmail }, store);
  return sendContract(draft.id, store, lead);
}

// ── Public signing (unauthenticated) ───────────────────────────────────────────

/**
 * Minimal fetch for the two public signing endpoints. No Authorization header,
 * no silent-refresh (there is no session); mirrors `apiFetch`'s error parsing
 * and its "only declare a JSON body when one is sent" rule.
 */
async function publicFetch<T>(path: string, init: RequestInit): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body !== undefined && init.body !== null && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const res = await classifiedFetch(`${API_BASE_URL}${path}`, { ...init, headers });
  if (!res.ok) {
    let code = 'UNKNOWN';
    let message: string | undefined;
    try {
      const body = (await res.json()) as { error?: string; message?: string };
      code = body.error ?? code;
      message = body.message;
    } catch {
      /* empty body */
    }
    throw new ApiError(res.status, code, message ?? errorMessage(code, res.status));
  }
  if (res.status === 204) return {} as T;
  return res.json() as Promise<T>;
}

/** Agreement shown to the signer before signing (GAP-8). `text` is null for a
 *  non-text template (`hasFile` lets it be viewed); `particulars` is the generated
 *  Schedule of Particulars; `terms`/`termsFileMatchesSignedHash` check the frozen file. */
export interface ContractAgreement {
  name: string;
  text: string | null;
  hasFile: boolean;
  particulars: ReadonlyArray<{ key: string; label: string; value: string; note?: string }>;
  terms: { name: string; sha256: string } | null;
  termsFileMatchesSignedHash: boolean | null;
}

/** The party issuing the agreement, for the signing-page wording. */
export interface ContractIssuer {
  name: string;
  type: 'individual' | 'organisation';
}

export interface PublicContract {
  id: string;
  status: string;
  template_id: string;
  /** The issuing practice - used to detect a practice-staff previewer (not the signer). */
  practice_id: string;
  agreement: ContractAgreement | null;
  issuer: ContractIssuer | null;
  /** Name the agreement was prepared for; shown as guidance on the signing form. */
  contactName: string | null;
  /** Of the schedule shown, sent back on signing so a changed schedule is refused (signing page only). */
  particularsFingerprint?: string | null;
}

export async function getPublicContract(token: string): Promise<PublicContract> {
  const res = await publicFetch<{
    contract: Omit<PublicContract, 'agreement' | 'issuer' | 'contactName' | 'particularsFingerprint'>;
    agreement: ContractAgreement | null;
    issuer: ContractIssuer | null;
    contactName: string | null;
    particularsFingerprint: string | null;
  }>(`/contracts/sign/${encodeURIComponent(token)}`, {});
  return {
    ...res.contract, agreement: res.agreement, issuer: res.issuer, contactName: res.contactName,
    particularsFingerprint: res.particularsFingerprint,
  };
}

export async function getContractPreview(
  contractId: string,
  store: TokenStore,
): Promise<PublicContract> {
  const res = await apiFetch<{
    contract: Omit<PublicContract, 'agreement' | 'issuer' | 'contactName'>;
    agreement: ContractAgreement | null;
    issuer: ContractIssuer | null;
    contactName: string | null;
  }>(`/contracts/${encodeURIComponent(contractId)}/preview`, {}, store);
  return { ...res.contract, agreement: res.agreement, issuer: res.issuer, contactName: res.contactName };
}

export async function fetchContractPreviewFile(
  contractId: string,
  store: TokenStore,
): Promise<ContractTemplateFileResult> {
  return fetchAuthenticatedFileBlob(`/contracts/${encodeURIComponent(contractId)}/preview/file`, store);
}

export interface SignContractResult {
  caseId: string | null;
  replay: boolean;
  /** The email the signer's portal account was created against (for account setup). */
  contactEmail: string | null;
  /** True for a signer row created by THIS request — picks the panel's copy variant. */
  accountCreated: boolean;
  /** True when the signer cannot sign in yet. Gates whether the screen offers account setup. */
  needsAccountActivation: boolean;
  /** True when this signed an update to an already-open case, not a new one
   *  (FB-0051) — the success screen must not say "your case has been created". */
  isCaseUpdate: boolean;
}

/**
 * Sign the contract. `idempotencyKey` MUST be stable across retries of the same
 * signing attempt - the server returns the SAME caseId (replay: true) for a
 * repeated key, guaranteeing exactly one case is created under retry.
 */
export async function signContract(
  token: string,
  signerName: string,
  particularsFingerprint: string | undefined,
  idempotencyKey: string,
): Promise<SignContractResult> {
  return publicFetch<SignContractResult>(`/contracts/sign/${encodeURIComponent(token)}`, {
    method: 'POST',
    headers: { 'Idempotency-Key': idempotencyKey },
    body: JSON.stringify({ signerName, particularsFingerprint }),
  });
}

/**
 * Authenticated preview of the rendered contract for a sent enquiry.
 * Returns `{ agreement: null }` when no sent contract exists.
 * `agreement.text` is null when the template is a binary (non-text) file.
 */
export async function getEnquiryContractText(
  enquiryId: string,
  store: TokenStore,
): Promise<{ agreement: ContractAgreement | null }> {
  return apiFetch<{ agreement: ContractAgreement | null }>(
    `/enquiries/${encodeURIComponent(enquiryId)}/contract-text`,
    {},
    store,
  );
}

export async function voidContract(
  contractId: string,
  reason: string,
  store: TokenStore,
): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/contracts/${encodeURIComponent(contractId)}/void`,
    { method: 'POST', body: JSON.stringify({ reason }) },
    store,
  );
}

export interface ContractAwaitingCase {
  id: string;
  signerName: string | null;
  signedAt: number | null;
}

export async function listContractsAwaitingCase(
  practiceId: string,
  store: TokenStore,
): Promise<ContractAwaitingCase[]> {
  const res = await apiFetch<{ contracts: ContractAwaitingCase[] }>(
    `/practices/${encodeURIComponent(practiceId)}/contracts-awaiting-case`,
    { method: 'GET' },
    store,
  );
  return res.contracts;
}

export async function convertContractToCase(
  contractId: string,
  store: TokenStore,
): Promise<{ caseId: string }> {
  return apiFetch<{ caseId: string }>(
    `/contracts/${encodeURIComponent(contractId)}/convert-to-case`,
    { method: 'POST' },
    store,
  );
}

/** Public - the prospect declines a sent (unsigned) agreement (item A). */
export async function declineContract(token: string, reason: string): Promise<void> {
  await publicFetch<{ declined: boolean }>(`/contracts/sign/${encodeURIComponent(token)}/decline`, {
    method: 'POST',
    body: JSON.stringify(reason.trim() === '' ? {} : { reason: reason.trim() }),
  });
}

export async function fetchContractPreviewDocument(
  contractId: string,
  store: TokenStore,
): Promise<ContractTemplateFileResult> {
  return fetchAuthenticatedFileBlob(`/contracts/${encodeURIComponent(contractId)}/preview/document`, store);
}
